import { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import Button from 'components/shared/Button';
import CircularProgress from 'components/shared/CircularProgress';
import Tabs from 'components/Tabs';
import useWorkflowPermissions from 'hooks/useWorkflowPermissions';
import Workflow from 'models/Workflow';
import { WorkflowActionIntent, WorkflowValuesInput } from 'services/api/workflows';
import { colors } from 'styles/theme/colors';
import { FONT_WEIGHT_MEDIUM } from 'styles/theme/textStyles';
import ClockIcon from 'svg/ClockIcon';
import DeactivatedUserIcon from 'svg/DeactivatedUserIcon';
import ErrorOccurredIcon from 'svg/ErrorOccurredIcon';
import NoteIconOutline from 'svg/NoteIconOutline';
import SSOErrorIcon from 'svg/SSOErrorIcon';

import { conjoin, labelForValue } from '../actionConfig';
import { VALUE_NAMES } from '../constants';
import DetailSlideout from '../DetailSlideout';

import AddCoverageModal from './AddCoverageModal';
import CoverageHistory from './CoverageHistory';
import {
  COVERAGE_TAB_LABELS,
  coverageForTab,
  coveragesOf,
  coverageTabLabel,
  coverageTabs,
  HISTORY_TAB,
  tabForFacet,
} from './coverageTabs';
import { blockedValueNames, EXTERNAL_PAYER_ID_VALUE, PAYER_LOGO_VALUE, readEligibility } from './eligibilityData';
import EligibilityDetailsHeader from './EligibilityDetailsHeader';
import EligibilityEmptyState from './EligibilityEmptyState';
import EligibilityFooter from './EligibilityFooter';
import EligibilityResultView from './EligibilityResultView';
import RerunConfirmModal from './RerunConfirmModal';
import SelectPayerFooter from './SelectPayerFooter';
import SelectPayerView from './SelectPayerView';

// The Insurance Eligibility Check slideout. Opened from the Payer Search / Insurance Verification
// rows; derives which of the six states to show from the two auto-check actions and renders the
// matching subview. Interactions run through the same complete/invoke handlers the card uses:
// picking a payer completes PayerSearch with its external_payer_id; Try Again re-runs BenefitsEligibility.

interface Props {
  open: boolean;
  onClose: () => void;
  workflow: Workflow;
  onComplete: (actionId: string, outputs: WorkflowValuesInput) => void;
  isCompleting: boolean;
  onInvoke: (actionId: string, invokeAction: WorkflowActionIntent) => void;
  isInvoking: boolean;
  onEditDetails?: () => void;
  // The coverage being verified (design 008 §3e); null on a referral with no coverages. Every
  // value this slideout renders belongs to one coverage, so an unscoped read would show a
  // sibling coverage's payer next to this one's results.
  facetId: string | null;
}

export default function EligibilityCheckSlideout({
  open,
  onClose,
  workflow,
  onComplete,
  isCompleting,
  onInvoke,
  isInvoking,
  onEditDetails,
  facetId,
}: Props) {
  const tabs = useMemo(() => coverageTabs(workflow), [workflow]);
  const [tab, setTab] = useState(() => tabForFacet(workflow, facetId));
  // Which row opened the slideout, so the tab only re-syncs when a DIFFERENT row opens it. Keyed on
  // the row rather than on the workflow: the referral reloads every few seconds while this is open, and
  // syncing on that would throw the reader off whichever coverage they were reading, mid-read.
  const openedFor = useRef(facetId);
  const [addingCoverage, setAddingCoverage] = useState(false);
  // The payer picked but not yet run. Lives here rather than in SelectPayerView because the action
  // that uses it sits in the slideout's pinned footer — and because it must reset when the shown
  // coverage changes, or a pick made on one coverage would arm the button on another.
  const [selectedPayerId, setSelectedPayerId] = useState<string | null>(null);
  const [confirmingRerun, setConfirmingRerun] = useState(false);
  // The row that opened the slideout decides which coverage it opens on, so clicking the secondary's
  // flagged verification lands on Secondary rather than on Primary with the reader to work out why.
  useEffect(() => {
    if (openedFor.current === facetId) return;

    openedFor.current = facetId;
    setTab(tabForFacet(workflow, facetId));
  }, [workflow, facetId]);

  // A rank can lose its slot while somebody is reading it: an untouched seeded coverage is archived
  // once extraction finds a real one (OLIO-3167), and the referral reloads under the reader. Falling
  // back keeps the strip from highlighting nothing while the body invites them to add the very
  // coverage they were reading.
  const activeTab = tab === HISTORY_TAB || tabs.some((option) => option.value === tab) ? tab : (tabs[0]?.value ?? tab);
  const selectedTab = tabs.find((option) => option.value === activeTab);
  const onHistory = activeTab === HISTORY_TAB;
  // Which coverage the body is showing. On a referral with no coverages this stays the facetId the
  // caller passed (null) — that referral has one run-level verification and no tabs at all.
  const coverage = tabs.length === 0 ? null : coverageForTab(workflow, activeTab);
  const shownFacetId = tabs.length === 0 ? facetId : (coverage?.id ?? null);

  // A payer picked on one coverage must not arm the run button on another — the footer's action
  // completes THAT coverage's payer search.
  useEffect(() => setSelectedPayerId(null), [shownFacetId]);

  // A tab whose coverage doesn't exist yet — the Secondary slot on a referral that only has a primary.
  // Distinct from a referral with NO coverages, which has no tabs and one run-level verification.
  const emptySlot = tabs.length > 0 && !coverage;
  // Which slot is empty decides the words. It is not always the secondary: a packet naming only a
  // secondary leaves the PRIMARY slot empty, and this used to invite a secondary either way
  // (OLIO-3141).
  const emptySlotLabel = coverageTabLabel(Number(activeTab));

  const { payerSearch, benefits, candidates, result, state } = readEligibility(workflow, shownFacetId);
  // Every write this slideout offers, answered in one place. Each rides the grant the API's policy
  // checks for it, so a reader is never shown a control that can only end in a 403 — and a new
  // condition on who may act is added here rather than at each control.
  const { canEdit, canEditAction } = useWorkflowPermissions();
  // Picking a payer completes PayerSearch, so the pick and the button that submits it both ride
  // that action's own grant. A view-only account keeps the candidate list — which payers the search
  // returned is the step's result, and reading a referral's results is the point of the account —
  // but the cards stop responding and the footer's Run button comes off rather than sitting there
  // to be clicked into a 403.
  const canSelectPayer = payerSearch ? canEditAction(payerSearch, workflow) : false;
  // Re-running is the verification's own write, so it rides that action's grant — the footer's
  // Re-Run and the failure states' Try Again are the same `rerun` and come off together. What they
  // sit beside stays: when the check last ran, its PDF, and the copy naming what went wrong are all
  // readings of the result.
  const canRerunCheck = benefits ? canEditAction(benefits, workflow) : false;
  // Filing a coverage has no action behind it — `createFacet` writes the referral — so it rides
  // what the header's edit menu asks for (InboundReferralPage): workflow-level edit, on a referral
  // that is not locked.
  const canAddCoverage = canEdit && !workflow.isLocked;
  // What the payer search searched for: this COVERAGE's payer name. `plan_type` is only the right
  // answer for a referral that has no coverages and whose verification is still run-level — one
  // created before OLIO-2890 stopped referral intake capturing a payer of its own.
  const payerName = shownFacetId
    ? workflow.valueFor(VALUE_NAMES.PAYER_NAME, { facetId: shownFacetId })?.value
    : workflow.valueFor(VALUE_NAMES.PLAN_TYPE)?.value;
  const memberId = workflow.valueFor(VALUE_NAMES.MEMBER_ID, { facetId: shownFacetId })?.value;
  // Committed at pick time (PayerSearch's candidates are gone by result time — see readEligibility).
  const payerLogo = (workflow.valueFor(PAYER_LOGO_VALUE, { facetId: shownFacetId })?.value as string) || undefined;
  // Until a check has run there is nothing to report and nothing to act on — no "last ran", no PDF,
  // and nothing to re-run. Suppress the whole bar rather than render an empty one: while the payer is
  // still being picked, SelectPayerView owns the primary action, and a second (dead) button beneath it
  // read as broken.
  const hasFooterContent = Boolean(benefits?.completedAt) || Boolean(result?.pdf?.eligibilityCheckId);
  const selectingPayer = state === 'select-payer';
  // What a parked check is waiting on, named by the API on the flagged action itself.
  const blockedValues = blockedValueNames({ payerSearch, benefits });

  const runCheck = (payerId: string) => {
    if (!payerSearch) return;
    // Carry the selected payer's logo forward so the result Overview can show it.
    const logo = candidates.find((c) => c.primaryPayerId === payerId)?.logo ?? '';
    onComplete(payerSearch.id, { [EXTERNAL_PAYER_ID_VALUE]: payerId, [PAYER_LOGO_VALUE]: logo });
  };
  const retry = () => {
    if (benefits) onInvoke(benefits.id, 'rerun');
  };
  const confirmRerun = () => {
    setConfirmingRerun(false);
    retry();
  };

  return (
    <DetailSlideout
      open={open}
      onClose={onClose}
      title={`${workflow.patientName}: Benefit Verification`}
      subtitle='Verifying patient has active insurance coverage and eligibility'
      tabs={
        tabs.length > 0 ? <Tabs tabs={tabs} selectedTab={selectedTab} onTabClick={(t) => setTab(t.value)} /> : undefined
      }
      hideHeaderBorder
      detailsBar={
        emptySlot || onHistory ? undefined : (
          <EligibilityDetailsHeader
            workflow={workflow}
            onEditDetails={onEditDetails}
            facetId={shownFacetId}
            coverageStatus={
              state === 'active-coverage' ? 'active' : state === 'no-active-coverage' ? 'inactive' : undefined
            }
          />
        )
      }
      footer={
        emptySlot || onHistory ? undefined : selectingPayer ? (
          !canSelectPayer ? undefined : (
            <SelectPayerFooter
              selectedId={selectedPayerId}
              onRun={() => selectedPayerId && runCheck(selectedPayerId)}
              isRunning={isCompleting}
            />
          )
        ) : !hasFooterContent ? undefined : (
          <EligibilityFooter
            workflowId={workflow.id}
            benefits={benefits}
            facetId={shownFacetId}
            pdfAvailable={Boolean(result?.pdf?.eligibilityCheckId)}
            onRerun={canRerunCheck ? () => setConfirmingRerun(true) : undefined}
            isRerunning={isInvoking}
          />
        )
      }>
      {onHistory ? (
        <CoverageHistory workflow={workflow} />
      ) : emptySlot ? (
        /* A coverage slot nobody has filled: an invitation, not a failure — so no status, no result,
           and no footer actions, because there is no check to act on yet. */
        <EligibilityEmptyState
          tone='neutral'
          icon={<NoteIconOutline width={40} height={45} color={colors.black75} />}
          title={`No ${emptySlotLabel} Insurance Added Yet`}
          body={
            // The invitation is the acting half of this state, so it goes with the button: a reader
            // told to "add the details below" with nothing below to add them in has been sent
            // somewhere that doesn't exist. What stays is the fact the slot is empty, which is the
            // reason they opened the tab.
            canAddCoverage ? (
              <>
                If this patient has a {emptySlotLabel.toLowerCase()} insurance plan,
                <br />
                add the details below to run a benefit verification
              </>
            ) : (
              `This referral has no ${emptySlotLabel.toLowerCase()} insurance on file.`
            )
          }
          action={
            canAddCoverage ? (
              <Button
                onClick={() => setAddingCoverage(true)}
                data-testid={`add-${emptySlotLabel.toLowerCase()}-insurance`}>
                + Add {emptySlotLabel} Insurance
              </Button>
            ) : undefined
          }
        />
      ) : (
        <>
          {selectingPayer && (
            <SelectPayerView
              candidates={candidates}
              searchTerm={payerName}
              selectedId={selectedPayerId}
              onSelect={setSelectedPayerId}
              canSelect={canSelectPayer}
            />
          )}

          {state === 'active-coverage' && result && (
            <EligibilityResultView result={result} active payerLogo={payerLogo} />
          )}
          {state === 'no-active-coverage' && result && (
            <EligibilityResultView result={result} active={false} payerLogo={payerLogo} />
          )}

          {state === 'payer-not-enrolled' && (
            <EligibilityEmptyState
              icon={<SSOErrorIcon width={56} height={56} />}
              title='Payer Not Enrolled in Eligibility Check'
              body={
                <>
                  Unfortunately, this patient’s payer is not enrolled in Olio’s automated eligibility check.
                  <br />
                  Reach out to the payer for details around this patient’s eligibility.
                </>
              }
            />
          )}

          {state === 'no-results' && (
            <EligibilityEmptyState
              icon={<DeactivatedUserIcon width={56} height={56} />}
              title='No Results Found'
              body={
                <>
                  This patient couldn’t be found with the provided information.
                  <br />
                  <Emphasis>
                    Please verify the member ID, name, and date of birth match the insurance card, then try again.
                  </Emphasis>
                </>
              }
              onTryAgain={canRerunCheck ? retry : undefined}
              isRetrying={isInvoking}
            />
          )}

          {/* The search came back with nothing to pick (OLIO-3426, QA). No retry offered: re-running the
              same query returns the same nothing, and what has to change is the payer name on the
              referral. Kept apart from 'no-results' because that one IS worth retrying — there the
              payer was found and the eligibility check missed the patient.
              Neutral, like `blocked` and unlike the three states above it: the search did its job and
              reported honestly, and a person fixing an input is an invitation rather than a failure.
              The opener is carried as a BUTTON gated on onEditDetails rather than named in the copy,
              for the reason `blocked` does the same — a viewer with no edit grant has no Edit Details
              link on the page, and telling them to use one sends them somewhere that isn't there
              (OLIO-2615/3141). Without the grant the sentence still stands on its own. */}
          {state === 'no-payer-match' && (
            <EligibilityEmptyState
              tone='neutral'
              icon={<DeactivatedUserIcon width={56} height={56} />}
              title='No Matching Payer Found'
              body={
                <>
                  No payer matched the insurance details provided for this patient.
                  <br />
                  <Emphasis>Please check the payer name against the patient’s insurance card.</Emphasis>
                </>
              }
              action={
                onEditDetails ? (
                  <Button onClick={onEditDetails} data-testid='eligibility-edit-payer'>
                    Edit Details
                  </Button>
                ) : undefined
              }
            />
          )}

          {state === 'error' && (
            <EligibilityEmptyState
              icon={<ErrorOccurredIcon width={56} height={56} />}
              title='Something Went Wrong'
              body={
                <>
                  Something went wrong while checking eligibility.
                  <br />
                  Please try again or contact support if the issue persists.
                </>
              }
              onTryAgain={canRerunCheck ? retry : undefined}
              isRetrying={isInvoking}
            />
          )}

          {/* Reachable only if the slideout was open when the run got canceled (poll flips the
          state under it) — the row-level entry links are suppressed for canceled. */}
          {state === 'canceled' && (
            <EligibilityEmptyState
              icon={<DeactivatedUserIcon width={56} height={56} />}
              title='Eligibility Check Canceled'
              body='This referral was canceled before the eligibility check finished.'
            />
          )}

          {state === 'running' && (
            <EligibilityEmptyState
              icon={<CircularProgress color={colors.blue100} size={32} />}
              title='Running eligibility check…'
              body='This can take a few moments. The results will appear here when the check completes.'
            />
          )}

          {/* The check is parked on information the engine can't derive (OLIO-3330). Names the
              missing fields from the action's own `unobtainableValues` rather than a list kept
              here — the API decides what a step requires, and a hardcoded copy would drift the
              first time the contract changed. Same substance as the checklist's FlaggedPrompt,
              which this slideout never reached: the eligibility row renders as its own component,
              so it bypassed the flagged copy every other step gets.

              No opener means no edit permission (OLIO-2615) — the copy still explains what is
              missing, but the link that would fix it isn't offered. */}
          {state === 'blocked' && (
            <EligibilityEmptyState
              icon={<NoteIconOutline width={40} height={45} color={colors.black75} />}
              tone='neutral'
              title='More information is needed to run a benefit verification'
              body={
                // The name list can be empty — the flag is what puts us here, not the names (see
                // deriveEligibilityState). Degrade to the same sentence the checklist falls back to
                // rather than rendering "This check can't run until  is added to the referral."
                blockedValues.length === 0 ? (
                  'This check needs more information before it can run.'
                ) : (
                  <>
                    This check can’t run until{' '}
                    <BlockedValues data-testid='eligibility-blocked-values'>
                      {conjoin(blockedValues.map((name) => labelForValue(workflow, name)))}
                    </BlockedValues>{' '}
                    {blockedValues.length > 1 ? 'are' : 'is'} added to the referral.
                  </>
                )
              }
              action={
                onEditDetails ? (
                  <Button onClick={onEditDetails} data-testid='eligibility-add-details'>
                    Add Details
                  </Button>
                ) : undefined
              }
            />
          )}

          {/* A clock, not the spinner: nothing is running yet, and a spinner over "hasn't run"
              read as work in progress. Matches how the checklist draws a step that hasn't
              started — never animated. */}
          {state === 'pending' && (
            <EligibilityEmptyState
              icon={<ClockIcon width={56} height={56} />}
              title='Eligibility check not started'
              body='This check hasn’t run yet. It will start once the required details are available.'
            />
          )}
        </>
      )}

      {addingCoverage && (
        <AddCoverageModal
          workflowId={workflow.id}
          onClose={() => setAddingCoverage(false)}
          label={emptySlotLabel}
          position={Number(activeTab)}
          // Only for a slot the vocabulary names. Past Secondary there is no sequence to claim, so
          // nothing is claimed and the API appends.
          claim={COVERAGE_TAB_LABELS[Number(activeTab)]}
          // Land on the coverage just added rather than back on the primary — the person filled that
          // form to watch this check start.
          //
          // Land on the coverage just added rather than back on the primary — the person filled
          // that form to watch this check start.
          //
          // Its own rank, not the end of the list: a coverage filed into the empty PRIMARY slot
          // leaves a list whose LAST entry is the pre-existing secondary, so counting the list sent
          // the person to the wrong coverage (OLIO-3141). Falls back to the slot just filled when
          // the saved payload has not caught up.
          onAdded={(saved) =>
            setTab(String(coveragesOf(saved).find((cov) => cov.position === Number(activeTab))?.position ?? activeTab))
          }
        />
      )}

      {confirmingRerun && (
        <RerunConfirmModal
          payerName={String(payerName ?? '')}
          memberId={String(memberId ?? '')}
          onConfirm={confirmRerun}
          onCancel={() => setConfirmingRerun(false)}
          isRerunning={isInvoking}
        />
      )}
    </DetailSlideout>
  );
}

// A bare <strong> takes the browser's 700, which is heavier than this brand's bold (Special
// Gothic is variable; FONT_WEIGHT_MEDIUM/500 is the theme's mapping) — still a <strong> element,
// the emphasis is real, it just takes the theme's weight instead of the browser default.
const Emphasis = styled.strong`
  font-weight: ${FONT_WEIGHT_MEDIUM};
`;

// The missing field names, emphasized inside the sentence rather than listed beneath it — the
// reader's question is "what is it waiting on", and the answer belongs in the copy they are
// already reading. Same weight treatment as Emphasis above, with a test hook so the names can be
// asserted without matching the whole sentence.
const BlockedValues = styled.span`
  color: ${colors.black100};
  font-weight: ${FONT_WEIGHT_MEDIUM};
`;
